import { useEffect, useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import { useAppConfig } from "../config/app-config";
import { logCustomEvent } from "../datadog";

export interface FeedbackSettings {
  hideUntil: string;
  registeredActions: Array<any>;
}

export const useFeedbackSettings = () => {
  const [appConfig] = useAppConfig();
  const feedbackConfig = appConfig!.ux.feedback.dialog;

  const initialValue = useMemo(
    () => getInitialValue(feedbackConfig.initialDelayDays),
    [feedbackConfig.initialDelayDays],
  );
  const [settings, setSettings] = useLocalStorage<FeedbackSettings>(
    "color-finder-feedback-settings",
    initialValue,
  );

  useEffect(() => {
    if (settings === initialValue) {
      setSettings(initialValue);
    }
  }, [settings, setSettings, initialValue]);

  const delayPassed = Date.now() > new Date(settings.hideUntil).getTime();
  const shouldShow =
    settings.registeredActions.length >= feedbackConfig.actionsCountThreshold &&
    delayPassed;

  const registerAction = (action: any) => {
    if (delayPassed) {
      setSettings({
        hideUntil: settings.hideUntil,
        registeredActions: [...settings.registeredActions, action],
      });
    }
  };
  const resetHideUntil = (delay: number) => {
    const hideUntil = new Date();
    hideUntil.setDate(hideUntil.getDate() + delay);
    setSettings({
      hideUntil: hideUntil.toISOString(),
      registeredActions: [],
    });
  };

  return {
    shouldShow,
    registerAction,
    onIgnore: () => {
      resetHideUntil(feedbackConfig.initialDelayDays);
      logCustomEvent("feedback-ignore");
    },
    onSubmit: (rating: number) => {
      resetHideUntil(feedbackConfig.submitDelayDays);
      logCustomEvent("feedback-submit", { rating });
    },
  };
};

const getInitialValue = (delayDays: number) => {
  const defaultHideUntil = new Date();
  defaultHideUntil.setDate(defaultHideUntil.getDate() + delayDays);
  return {
    hideUntil: defaultHideUntil.toISOString(),
    registeredActions: [],
  };
};
